/**
 * Shared type vocabulary for the drafting / state-machine cluster.
 *
 * Extracted from `draftingHelpers.ts` so the sibling state-machine
 * modules can import the core types (`DraftPoint`, `DraftCommandId`,
 * `DraftStep`, `DraftSession`, `DraftCommitResult`) without
 * re-importing the parent helpers module — breaks the depcruise cycles
 *
 *   draftingHelpers.ts ↔ draftPreviewEntities.ts
 *   draftingHelpers.ts ↔ draftStateMachine.ts
 *   draftingHelpers.ts ↔ draftModifyStateMachine.ts
 *   draftingHelpers.ts ↔ draftStateMachine.blockAuthoring.ts
 *   draftingHelpers.ts ↔ draftStateMachine.dimensions.ts
 *   geomConstraintPicks.ts → ... → draftingHelpers.ts (transitive)
 *
 * The parent `draftingHelpers.ts` re-exports every symbol so existing
 * import paths keep working. New code should prefer importing directly
 * from this module to keep the dependency graph acyclic.
 */

import type { EditorCommand, EntityPreview } from "@dxf-ai-editor/contracts";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

export type DraftPoint = {
  x: number;
  y: number;
};

export type DraftCommandId =
  | "LINE"
  | "POINT"
  // BASE / БАЗА — set the drawing insertion base point ($INSBASE). Single
  // point-pick command (AutoCAD "Enter base point <0,0,0>:").
  | "BASE"
  | "CIRCLE"
  | "ARC"
  | "ELLIPSE"
  | "PLINE"
  | "RECTANG"
  | "DONUT"
  | "POLYGON"
  | "TEXT"
  | "MTEXT"
  | "BLOCK"
  | "INSERT"
  | "BEDIT"
  | "BCLOSE"
  | "BSAVE"
  | "ATTDEF"
  | "DDATTE"
  | "ATTEDIT"
  | "EATTEDIT"
  | "ARRAYRECT"
  | "ARRAYPOLAR"
  | "ARRAYPATH"
  | "ARRAYEDIT"
  | "HATCH"
  | "HATCHEDIT"
  | "DIMENSION"
  | "DIMALIGNED"
  | "DIMANGULAR"
  | "DIM3POINT"
  | "DIMRADIUS"
  | "DIMDIAMETER"
  | "DIMORDINATE"
  | "DIMBASELINE"
  | "DIMCONTINUE"
  | "MLEADER"
  | "MLEADEREDIT"
  | "REVCLOUD"
  | "WIPEOUT"
  | "SPLINE"
  | "MVIEW"
  // VPCLIP-INTERACTIVE — interactive rectangular clip-boundary pick
  // (select the target viewport, then two corner points).
  | "VPCLIP"
  | "MOVE"
  | "COPY"
  | "MIRROR"
  | "ROTATE"
  | "SCALE"
  | "OFFSET"
  | "STRETCH"
  | "TRIM"
  | "EXTEND"
  | "FILLET"
  | "CHAMFER"
  | "BREAK"
  | "JOIN"
  | "EXPLODE"
  | "LENGTHEN"
  | "GCCONSTRAINT"
  | "BPARAM-LINEAR"
  | "BPARAM-POINT"
  | "BPARAM-BASEPOINT"
  | "BPARAM-VISIBILITY"
  | "BPARAM-LOOKUP"
  | "BPARAM-FLIP"
  | "BPARAM-ALIGNMENT"
  | "BPARAM-POLAR"
  | "BPARAM-XY"
  | "BPARAM-ROTATION"
  | "BACTION-MOVE"
  | "BACTION-SCALE"
  | "BACTION-STRETCH"
  | "BACTION-POLAR-STRETCH"
  | "BACTION-ROTATION"
  | "BACTION-FLIP"
  | "BACTION-ARRAY"
  | "BACTION-LOOKUP";

export type DraftStep =
  | "line-first-point"
  | "line-next-point"
  | "point-location"
  // BASE — pick the drawing insertion base point ($INSBASE).
  | "insbase-point"
  | "circle-center"
  | "circle-radius"
  | "circle-diameter"
  | "circle-ready"
  | "circle-2p-first-point"
  | "circle-2p-second-point"
  | "circle-3p-second-point"
  | "circle-3p-third-point"
  | "donut-inner-d"
  | "donut-outer-d"
  | "donut-center"
  | "arc-start"
  | "arc-second"
  | "arc-end"
  | "arc-ready"
  | "arc-sce-center"
  | "arc-sce-end-angle"
  | "ellipse-center"
  | "ellipse-major-end"
  | "ellipse-minor-distance"
  | "polyline-first-point"
  | "polyline-next-point"
  | "polyline-width-start"
  | "polyline-width-end"
  | "rectangle-first-corner"
  | "rectangle-second-corner"
  | "rectangle-ready"
  | "rectangle-d-length"
  | "rectangle-d-width"
  | "rectangle-d-place"
  | "polygon-sides-input"
  | "polygon-mode-prompt"
  | "polygon-center"
  | "polygon-radius"
  | "text-insertion-point"
  | "text-height"
  | "text-rotation"
  | "text-content"
  | "mtext-first-corner"
  | "mtext-second-corner"
  | "mtext-height"
  | "mtext-content"
  | "block-name"
  | "block-insertion-point"
  | "block-scale"
  | "block-rotation"
  | "block-ready"
  | "arrayrect-select"
  | "arrayrect-base-point"
  | "arrayrect-count"
  | "arrayrect-spacing"
  | "arraypolar-select"
  | "arraypolar-center"
  | "arraypolar-count"
  | "arraypolar-fill"
  | "arraypath-select"
  | "arraypath-path"
  | "arraypath-method"
  | "arraypath-params"
  | "arrayedit-select"
  | "block-select-objects"
  | "block-base-point"
  | "block-dialog"
  | "insert-dialog"
  | "insert-specify-point"
  | "insert-specify-scale"
  | "insert-specify-rotation"
  | "insert-collect-attribs"
  | "bedit-prompt-name"
  | "bedit-confirm-create"
  | "bclose-confirm"
  | "attdef-dialog"
  | "ddatte-select"
  | "ddatte-dialog"
  | "eattedit-select"
  | "eattedit-dialog"
  | "hatch-pattern"
  | "hatch-first-corner"
  | "hatch-second-corner"
  | "hatch-ready"
  | "hatch-mode-prompt"
  | "hatch-pick-internal-point"
  | "hatch-pick-additional"
  // HATCHEDIT — select a placed HATCH then prompt for pattern / scale / angle
  // (mirrors the ARRAYEDIT / MLEADEREDIT select-then-edit shape).
  | "hatchedit-select"
  | "hatchedit-pattern"
  | "hatchedit-scale"
  | "hatchedit-angle"
  | "dimension-first-point"
  | "dimension-second-point"
  | "dimension-line-point"
  | "dimension-ready"
  | "dimaligned-first-point"
  | "dimaligned-second-point"
  | "dimaligned-line-point"
  | "dimangular-first-line"
  | "dimangular-second-line"
  | "dimangular-arc-point"
  | "dim3point-vertex"
  | "dim3point-first-arm"
  | "dim3point-second-arm"
  | "dim3point-arc-point"
  | "dimradius-select-arc"
  | "dimradius-dim-line"
  | "dimdiameter-select-arc"
  | "dimdiameter-dim-line"
  | "dimordinate-feature-point"
  | "dimordinate-leader-end"
  | "dimbaseline-second-point"
  | "dimcontinue-second-point"
  | "mleader-arrow-point"
  | "mleader-landing-point"
  | "mleader-content"
  | "mleaderedit-select"
  | "mleaderedit-options"
  | "mleaderedit-add-leader-arrow"
  | "mleaderedit-remove-pick"
  | "revcloud-first-corner"
  | "revcloud-second-corner"
  | "revcloud-ready"
  // REVCLOUD-MODES — Arc length / Style sub-prompts + Polygonal /
  // Freehand / Object creation-method flows.
  | "revcloud-arc-length"
  | "revcloud-style"
  | "revcloud-polygonal-next"
  | "revcloud-freehand-path"
  | "revcloud-object-pick"
  | "revcloud-object-reverse"
  | "wipeout-first-corner"
  | "wipeout-second-corner"
  | "wipeout-ready"
  | "spline-fit-first"
  | "spline-fit-next"
  | "viewport-first-corner"
  | "viewport-second-corner"
  | "viewport-ready"
  // VPCLIP-INTERACTIVE — AutoCAD's real VPCLIP first prompt is "Select
  // viewport to clip:"; `vpclip-select-viewport` is that entity pick.
  // The two corner picks that follow mirror `viewport-first-corner` /
  // `-second-corner` exactly (a rectangular boundary is two diagonal
  // paper-space points), but commit `geometry/viewport-clip-set`
  // against the SELECTED viewport rather than creating a new one.
  | "vpclip-select-viewport"
  | "vpclip-first-corner"
  | "vpclip-second-corner"
  | "move-select"
  | "move-base-point"
  | "move-destination"
  | "copy-select"
  | "copy-base-point"
  | "copy-destination"
  | "mirror-select"
  | "mirror-first-point"
  | "mirror-second-point"
  | "rotate-select"
  | "rotate-base-point"
  | "rotate-angle"
  | "rotate-ref-start"
  | "rotate-ref-end"
  | "rotate-ref-new-angle"
  | "scale-select"
  | "scale-base-point"
  | "scale-factor"
  | "offset-distance"
  | "offset-select-entity"
  | "offset-side"
  | "stretch-select-first"
  | "stretch-select-second"
  | "stretch-base"
  | "stretch-destination"
  | "trim-select-edges"
  | "trim-pick-entity"
  | "extend-select-edges"
  | "extend-pick-entity"
  | "fillet-radius"
  | "fillet-first-entity"
  | "fillet-second-entity"
  | "chamfer-distances"
  | "chamfer-first"
  | "chamfer-second"
  | "break-select"
  | "break-first-point"
  | "break-second-point"
  | "join-select"
  | "explode-select"
  | "lengthen-select"
  | "lengthen-mode"
  | "lengthen-value"
  | "lengthen-pick-end"
  | "geom-constraint-pick-first"
  | "geom-constraint-pick-second"
  | "geom-constraint-pick-more"
  | "geom-constraint-pick-reference-line"
  | "param-linear-base"
  | "param-linear-end"
  | "param-linear-label"
  | "param-point-place"
  | "param-basepoint-place"
  | "param-visibility-place"
  | "param-lookup-place"
  | "param-flip-base"
  | "param-flip-end"
  | "param-flip-label"
  | "param-polar-base"
  | "param-polar-end"
  | "param-polar-label"
  | "param-xy-base"
  | "param-xy-end"
  | "param-xy-label"
  | "param-rotation-base"
  | "param-rotation-radius-end"
  | "param-rotation-label"
  | "param-alignment-base"
  | "param-alignment-end"
  | "action-move-pick-parameter"
  | "action-move-select-objects"
  | "action-move-place"
  | "action-scale-pick-parameter"
  | "action-scale-select-objects"
  | "action-scale-place"
  | "action-stretch-pick-parameter"
  | "action-stretch-select-objects"
  | "action-stretch-frame-corner-1"
  | "action-stretch-frame-corner-2"
  | "action-stretch-place"
  | "action-polar-stretch-pick-parameter"
  | "action-polar-stretch-select-objects"
  | "action-polar-stretch-frame-corner-1"
  | "action-polar-stretch-frame-corner-2"
  | "action-polar-stretch-place"
  | "action-rotation-pick-parameter"
  | "action-rotation-select-objects"
  | "action-rotation-place"
  | "action-flip-pick-parameter"
  | "action-flip-select-objects"
  | "action-flip-place"
  | "action-array-pick-parameter"
  | "action-array-select-objects"
  | "action-array-place"
  | "action-lookup-pick-parameter"
  | "action-lookup-select-objects"
  | "action-lookup-place";

export type DraftSession = {
  command: DraftCommandId;
  step: DraftStep;
  spaceId: string;
  points: DraftPoint[];
  hoverPoint: DraftPoint | null;
  closed: boolean;
  options: Record<string, string>;
};

export type DraftCommitResult = {
  entity?: EntityPreview;
  geometryCommand?: EditorCommand;
  geometryCommands?: EditorCommand[];
  summary: string;
  commandText: string;
  // feedOnly = true: skip the entity-create / geometry-dispatch path
  // and just push `summary` to the command-line feed. Used when the
  // state machine wants to surface a validation rejection (e.g. BEDIT
  // on a reserved layout block name) without mutating the document.
  feedOnly?: boolean;
};

/**
 * Result returned by every per-step advance callback.
 *
 * `null` is reserved for the dispatcher's "not handled, fall through"
 * sentinel — distinct from `{ nextSession: null }` which is the legitimate
 * "session ended" result for terminal steps.
 *
 * Lives here (not in `advanceDispatch.ts`) so helper modules like
 * `advanceHelpers.ts` can reference it without forming an import cycle
 * back to the dispatcher.
 */
export type AdvanceResult = {
  nextSession: DraftSession | null;
  commit?: DraftCommitResult;
};

/**
 * Per-call context shared across all per-step callbacks for the
 * point dispatcher. Mirrors the parameters threaded through
 * `advanceDraftSessionWithPoint`.
 *
 * Lives here (not in `advanceDispatch.ts`) so helper / registry
 * modules can reference the type without forming an import cycle.
 */
export interface AdvanceWithPointContext {
  readonly activeSpaceId: string;
  readonly existingEntities: ReadonlyArray<EntityPreview>;
  readonly geometryEntities: ReadonlyArray<DrawingEntity>;
  /**
   * VPCLIP-INTERACTIVE — the active layout's `LayoutPageSetup.origin`
   * (world-space position of the paper sheet's lower-left corner), or
   * `null` when the active space has no page setup (a synthetic /
   * non-layout space). Threaded through so `vpclip-select-viewport`'s
   * "which viewport is under this click" hit-test can call the SAME
   * `pointInViewport(paperPt, vp, paperOrigin)` choke point
   * `viewportContextController.ts`'s double-click-to-enter-VP hit-test
   * already uses (one truth — CLAUDE.md PRIORITY #2), instead of
   * re-deriving the origin convention. Every other step ignores this
   * field; `null` is a safe default (identical to the origin every
   * OTHER caller of `pointInViewport` falls back to for a non-layout
   * space).
   */
  readonly paperOrigin?: { readonly x: number; readonly y: number } | null;
}

/**
 * Per-call context shared across all per-step callbacks for the
 * text dispatcher. Mirrors the parameters threaded through
 * `advanceDraftSessionWithText`.
 *
 * `text` is pre-trimmed; `normalizedText` is `text.toUpperCase()`.
 */
export interface AdvanceWithTextContext {
  readonly text: string;
  readonly normalizedText: string;
  readonly activeSpaceId: string;
  readonly existingEntities: ReadonlyArray<EntityPreview>;
  /**
   * Typed geometry entities of the active space — mirrors
   * `AdvanceWithPointContext.geometryEntities`. Consumed by handlers
   * that must validate a typed entity id against real primitives
   * (e.g. REVCLOUD Object mode's closed-outline check — the preview
   * list carries no geometric primitives to trace).
   */
  readonly geometryEntities: ReadonlyArray<DrawingEntity>;
  /** See {@link AdvanceWithPointContext.paperOrigin}. */
  readonly paperOrigin?: { readonly x: number; readonly y: number } | null;
}

export type AdvanceWithPointFn = (
  session: DraftSession,
  point: DraftPoint,
  ctx: AdvanceWithPointContext,
) => AdvanceResult | null;

export type AdvanceWithTextFn = (
  session: DraftSession,
  ctx: AdvanceWithTextContext,
) => AdvanceResult | null;

// ── Tiny helper used across the cluster ──────────────────────────────
//
// Extracted alongside the type vocabulary so `draftPreviewEntities.ts`
// can pull it in without back-importing `draftingHelpers.ts`.

export function distanceBetween(left: DraftPoint, right: DraftPoint): number {
  return Math.hypot(right.x - left.x, right.y - left.y);
}

/**
 * Loose pick tolerance (drawing units) for state-machine-layer point
 * picks that run WITHOUT camera access (the draft dispatchers receive
 * world coordinates only). This is the single source of truth for the
 * "8 drawing units" convention established by `findParameterAtPoint`
 * (draftStateMachine.blockAuthoring.ts) — itself mirroring the 8 px
 * screen-space pickbox (`DEFAULT_PICKBOX_PX`, scene3d/overlays).
 * Callers that know the camera zoom can pass a tighter
 * pixel-equivalent value instead (e.g. `8 / cameraZoom`).
 */
export const DRAFT_POINT_PICK_TOLERANCE = 8;
